/**
 * The ready screen (#573): ONE summary of what needs the learner across the whole
 * PDF, and one Start button - not a list of pages.
 *
 * The count is lines still unresolved, summed over the pages' transcriptions (the
 * same queries the check itself uses, so they share a cache). It is a count of
 * things to look at - never of how much the learner knows. Start opens the first
 * page still awaiting the learner's check, and reads "Continue" once any page is already confirmed; what is recorded from there on is
 * recorded by `PageReview`, unchanged.
 */

import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { HomeworkUploadResponse } from '@davinci/contracts';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';

type UploadPage = HomeworkUploadResponse['pages'][number];

export function ReadySummary({ pages }: { readonly pages: readonly UploadPage[] }): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const readable = pages.filter((page) => page.reviewStage !== 'unreadable');
  const transcriptions = useQueries({
    queries: readable.map((page) => ({
      queryKey: queryKeys.homeworkTranscription(page.pageId),
      queryFn: () => api.getHomeworkTranscription(page.pageId),
    })),
  });
  const counted = transcriptions.every((query) => query.data !== undefined || query.isError);
  // A failed fetch is not zero lines: with one in doubt no count is claimed.
  const loadFailed = transcriptions.some((query) => query.isError);
  const unresolvedByPage = new Map<string, number>();
  readable.forEach((page, index) => {
    const regions = transcriptions[index]?.data?.regions ?? [];
    unresolvedByPage.set(page.pageId, regions.filter((region) => !region.resolved).length);
  });
  const needing = [...unresolvedByPage.values()].reduce((sum, n) => sum + n, 0);
  const first = pages.find((page) => page.reviewStage === 'awaiting_review');
  // Back mid-homework (#660): the first page still awaiting the learner is where the work stopped, so the button says so.
  const returning = pages.some((page) => page.reviewStage === 'confirmed');

  const showCount = readable.length > 0 && counted && !loadFailed && needing > 0;
  return (
    <section className="hw-panel hw-ready" aria-labelledby="homework-ready">
      <h1 id="homework-ready">
        {showCount ? (
          <>
            <span className="hw-ready-n" dir="ltr">
              {needing}
            </span>{' '}
            {t.readyWords(needing)}
          </>
        ) : readable.length === 0 ? (
          t.pagesNotRead(pages.length)
        ) : !counted ? (
          t.readyCounting
        ) : loadFailed ? (
          t.pagesNotLoaded
        ) : (
          t.readyNothingLeft
        )}
      </h1>
      <p>{t.readyAcross(pages.length)}</p>
      {readable.length > 0 && readable.length < pages.length ? (
        <p role="status">{t.pagesNotRead(pages.length - readable.length)}</p>
      ) : null}
      <ul className="hw-strip">
        {pages.map((page) => {
          const flagged = (unresolvedByPage.get(page.pageId) ?? 0) > 0;
          return (
            <li
              key={page.pageId}
              className={`hw-mini ${flagged ? 'hw-mini-flag' : ''}`}
              aria-label={`${t.pageN(page.pageIndex + 1)}: ${t.stage(page.reviewStage)}`}
            >
              {flagged ? <span className="hw-mini-dot" aria-hidden="true" /> : null}
            </li>
          );
        })}
      </ul>
      {first !== undefined ? (
        <Link className="hw-panel-primary" to={`?page=${encodeURIComponent(first.pageId)}`}>
          {returning ? t.continueCheck : t.startCheck}
        </Link>
      ) : null}
    </section>
  );
}
